"use client"

import { useState, useEffect } from "react"
import { Container, Row, Col, Card, Button, Collapse, Accordion, Modal } from "react-bootstrap"
import { Rocket, User, CreditCard, Smartphone, Scale, Code, ChevronDown, ChevronUp } from "lucide-react"
import * as Yup from "yup"
import PopupModal from "../common/PopUpModel/PopUpModel.jsx"
import TextInput from "../common/FormFields/TextInput.jsx"
import TextAreaInput from "../common/FormFields/TextareaInput.jsx"
import AsyncSelectDropdown from "../common/CustomAsyncDropdown.jsx"
import { getContactPersons, getContactPersonById } from "../../../services/ContactPersonService.js"
import ContactInfo from "../Student/Dashboard/ContactInfo.jsx"

const HelpCenter = () => {
  // State to manage which topic is expanded
  const [expandedTopic, setExpandedTopic] = useState(null)
  const [activeCard, setActiveCard] = useState(null)
  const [activeArticle, setActiveArticle] = useState(null)
  const [showModal, setShowModal] = useState(false)

  // Contact person states
  const [contactPersons, setContactPersons] = useState([])
  const [selectedContactPerson, setSelectedContactPerson] = useState(null)
  const [showContactModal, setShowContactModal] = useState(false)
  const [contactPersonDetails, setContactPersonDetails] = useState(null)
  const [loading, setLoading] = useState(false)

  // Fetch contact persons on component mount
  useEffect(() => {
    const fetchContactPersons = async () => {
      try {
        const response = await getContactPersons()
        const formattedOptions = response.data.map((person) => ({
          value: person.id,
          label: person.name,
        }))
        setContactPersons(formattedOptions)
      } catch (error) {
        console.error("Error fetching contact persons:", error)
      }
    }

    fetchContactPersons()
  }, [])

  // Handle contact person selection
  const handleContactPersonSelect = async (selectedOption) => {
    if (selectedOption) {
      setLoading(true)
      try {
        const response = await getContactPersonById(selectedOption.value)
        setContactPersonDetails(response.data)
        setSelectedContactPerson(selectedOption)
        setShowContactModal(true)
      } catch (error) {
        console.error("Error fetching contact person details:", error)
      } finally {
        setLoading(false)
      }
    }
  }

  // Initial form values
  const initialValues = {
    name: "",
    email: "",
    phone: "",
    course: "",
    message: "",
    formTitle: "Help Center Form",
  }

  // Form validation schema
  const validationSchema = Yup.object({
    name: Yup.string().required("Name is required"),
    email: Yup.string().email("Invalid email address").required("Email is required"),
    phone: Yup.string().required("Phone is required"),
    course: Yup.string().required("Course is required"),
    message: Yup.string().required("Message is required"),
  })

  const handleSubmit = (values) => {
    console.log("Form Data:", values)
    setShowModal(false)
  }

  // Help topics data
  const helpTopics = [
    {
      id: 1,
      title: "Getting Started",
      icon: Rocket,
      description: "Articles to get you up and running quick and easy",
      subTopics: ["Quick Start Guide", "Account Setup", "Basic Configuration", "First Steps Tutorial"],
    },
    {
      id: 2,
      title: "My Account",
      icon: User,
      description: "How to manage your account and its features",
      subTopics: ["Profile Settings", "Security Options", "Notification Preferences", "Account Verification"],
    },
    {
      id: 3,
      title: "Billing & Payments",
      icon: CreditCard,
      description: "Information about how we charge you for our services",
      subTopics: ["Payment Methods", "Billing Cycles", "Invoice History", "Subscription Management"],
    },
    {
      id: 4,
      title: "Mobile App",
      icon: Smartphone,
      description: "Documentation and troubleshooting our mobile app",
      subTopics: ["Installation Guide", "App Features", "Troubleshooting", "Mobile FAQ"],
    },
    {
      id: 5,
      title: "Copyright & Legal",
      icon: Scale,
      description: "Important information about how we handle your privacy and data",
      subTopics: ["Terms of Service", "Privacy Policy", "Data Protection", "Cookie Policy"],
    },
    {
      id: 6,
      title: "Developers",
      icon: Code,
      description: "Developer documentation and integration features",
      subTopics: ["API Documentation", "Integration Guide", "Code Examples", "Developer Tools"],
    },
  ]

  // Popular articles data
  const popularArticles = [
    {
      id: "1",
      title: "How to Create an Account",
      content: "Learn the step-by-step process of creating and setting up your account...",
      readTime: "5 min read",
    },
    {
      id: "2",
      title: "Best Tips for a 5-Day Free Trial",
      content: "Make the most of your free trial with these proven strategies...",
      readTime: "8 min read",
    },
    {
      id: "3",
      title: "How Our Pricing Plans Work",
      content: "Detailed breakdown of our pricing structure and available plans...",
      readTime: "6 min read",
    },
    {
      id: "4",
      title: "How Can I Get My Article Featured",
      content: "Guidelines and requirements for getting your content featured...",
      readTime: "4 min read",
    },
  ]

  return (
    <div className="help-center" style={{ minHeight: "100vh"}}>
      {/* Header Section */}
      <div className="header-section py-4">
        <Container>
          <h2 className="text-center text-dark mb-4">How can we help?</h2>
          
          {/* Contact Person Dropdown */}
          <div className="contact-dropdown-container mx-auto mt-4" style={{ maxWidth: "600px" }}>
            <label className="form-label text-dark mb-2">Search For Contact Person:</label>
            <AsyncSelectDropdown
              options={contactPersons}
              value={selectedContactPerson}
              onChange={handleContactPersonSelect}
              placeholder="Search and select a contact person..."
              isMulti={false}
            />
          </div>
        </Container>
      </div>

      {/* Main Content */}
      <Container className="py-1" >
        <Row>
          {/* Help Topics Section */}
          <Col md={8}>
            <h3 className="mb-4 text-dark">Help Topics</h3>
            <Row xs={1} md={1} className="g-4" style={{ marginBottom: "20px" }}>
              {helpTopics.map((topic) => (
                <Col key={topic.id}>
                  <Card className="h-100 border-0 shadow-sm mb-0">
                    <Card.Body style={{ padding: "1.5rem" }}>
                      <div
                        className="d-flex align-items-start cursor-pointer"
                        onClick={() => setExpandedTopic(expandedTopic === topic.id ? null : topic.id)}
                      >
                        <div className="icon-wrapper me-3">
                          <topic.icon
                            size={24}
                            className=""
                            style={{
                              color: "#f97316",
                            }}
                          />
                        </div>
                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between align-items-center">
                            <Card.Title className="text-dark">{topic.title}</Card.Title>
                            {expandedTopic === topic.id ? (
                              <ChevronUp size={20} className="text-muted" />
                            ) : (
                              <ChevronDown size={20} className="text-muted" />
                            )}
                          </div>
                          <Card.Text className="text-muted">{topic.description}</Card.Text>
                        </div>
                      </div>
                      <Collapse in={expandedTopic === topic.id}>
                        <div className="mt-3">
                          <hr />
                          <ul className="list-unstyled mb-0">
                            {topic.subTopics.map((subTopic, index) => (
                              <li key={index} className="py-2">
                                <a href="#!" className="text-decoration-none para-help">
                                  {subTopic}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </Collapse>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Col>

          {/* Sidebar */}
          <Col md={4}>
            {/* Popular Articles Accordion */}
            <Card className="border-0 shadow-sm mb-4">
              <Card.Body style={{ padding: "1.5rem" }}>
                <h4 className="mb-3 text-dark">Popular Articles</h4>
                <Accordion flush>
                  {popularArticles.map((article) => (
                    <Accordion.Item key={article.id} eventKey={article.id}>
                      <Accordion.Header>{article.title}</Accordion.Header>
                      <Accordion.Body style={{ border: "none !important", padding: "1rem" }}>
                        <p className="mb-2">{article.content}</p>
                        <small className="text-muted">{article.readTime}</small>
                      </Accordion.Body>
                    </Accordion.Item>
                  ))}
                </Accordion>
              </Card.Body>
            </Card>

            {/* Need Support Section */}
           
          </Col>
           <Card className="border-0 shadow-sm mt-4 mb-4">
              <Card.Body >
                <h4 className="mb-3">Need Support?</h4>
                <p className="text-muted">
                  Can't find the answer you're looking for? Don't worry, our team is here to help!
                </p>
                <Button
                  variant="info"
                  className="w-100 text-white"
                  style={{ backgroundColor: "#f97316", borderColor: "#f97316" }}
                  onClick={() => setShowModal(true)}
                >
                  Contact Support
                </Button>
              </Card.Body>
            </Card>
        </Row>
      </Container>

      {/* Contact Support Modal */}
      <PopupModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
        title="Contact Support"
        formFields={[
          () => <TextInput label="Name" name="name" />,
          () => <TextInput label="Email" name="email" type="email" />,
          () => <TextInput label="Phone" name="phone" />,
          () => <TextInput label="Course" name="course" />,
          () => <TextAreaInput label="Issues" name="message" placeholder="Enter your issue here..." rows={3} />,
        ]}
      />

      {/* Contact Person Details Modal */}
     <Modal show={showContactModal} onHide={() => setShowContactModal(false)} size="md" centered>
  <Modal.Header closeButton>
    <Modal.Title className="fw-bold">Contact Person</Modal.Title>
  </Modal.Header>

  <Modal.Body style={{ padding: "2rem" }}>
    {loading ? (
      <div className="text-center py-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    ) : contactPersonDetails ? (
      <div className="px-2">
        <div className="mb-4">
          <h6 className="mb-1 text-secondary">Name</h6>
          <p className="fs-6 text-dark mb-0">{contactPersonDetails.name}</p>
        </div>

        <div className="mb-4">
          <h6 className="mb-1 text-secondary">Email</h6>
          <p className="fs-6 text-dark mb-0">{contactPersonDetails.email}</p>
        </div>

        <div className="mb-2">
          <h6 className="mb-1 text-secondary">Phone</h6>
          <p className="fs-6 text-dark mb-0">{contactPersonDetails.phone}</p>
        </div>
      </div>
    ) : (
      <p className="text-muted">No contact details available.</p>
    )}
  </Modal.Body>

  <Modal.Footer>
    <Button variant="outline-primary" onClick={() => setShowContactModal(false)}>
      Close
    </Button>
  </Modal.Footer>
</Modal>

<ContactInfo/>

    </div>
  )
}

export default HelpCenter
